// pages/CategoriesPage.tsx
import { useState, useMemo } from "react";
import { Plus, Search, MoreVertical, Tag, Filter, X, Star } from "lucide-react";
import { useNavigate } from "react-router-dom";

import MenuModal from "../../../components/ui/MenuModal";
import DeleteConfirmationModal from "../../../components/ui/DeleteConfirmationModal";
import PageLayout from "../../../layouts/PageListLayout";
import FlexContainer from "../../../components/ui/FlexContainer";
import { alertSuccess, alertError } from "../../../helpers/alertError";
import { useAuth, useCategories } from "@cabukaka/hooks";
import CategoriesList from "../../../components/features/categories/CategoriesList";
import { Category } from "@cabukaka/types";
import SectorsTabLayout from "../../../layouts/SectorsTabLayout";
import useCustomParams from "../../../hooks/params/useCustomParams";


export default function CategoriesPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const role = user?.role;
    const { categories, deleteCategory, deleteManyCategories, updateCategory } = useCategories();
    const { sector: sectorSlug } = useCustomParams();

    const [searchTerm, setSearchTerm] = useState("");
    const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
    const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);
    const [itemsToDelete, setItemsToDelete] = useState<string[] | null>(null);
    const [itemsToFeature, setItemsToFeature] = useState<Category[] | null>(null);
    const [itemsToUnfeature, setItemsToUnfeature] = useState<Category[] | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isFeaturing, setIsFeaturing] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [showFeatureConfirm, setShowFeatureConfirm] = useState(false);
    const [showUnfeatureConfirm, setShowUnfeatureConfirm] = useState(false);

    const filteredCategories = useMemo(() => {
        let result = categories;

        if (sectorSlug) {
            result = result.filter(c => c.sector?.slug === sectorSlug);
        }

        if (searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase().trim();
            result = result.filter(c =>
                c.name?.toLowerCase().includes(searchLower) ||
                c.slug?.toLowerCase().includes(searchLower)
            );
        }

        return result;
    }, [categories, searchTerm, sectorSlug]);

    const stats = useMemo(() => ({
        total: categories.length,
        filteredTotal: filteredCategories.length,
        featured: categories.filter(c => c.isFeatured).length
    }), [categories, filteredCategories]);

    const handleDelete = async () => {
        if (!categoryToDelete?.id) return;
        setIsDeleting(true);
        try {
            await deleteCategory(categoryToDelete.id);
            setCategoryToDelete(null);
            alertSuccess("Catégorie supprimée avec succès");
        } catch (err) {
            console.error(err)
            alertError("Erreur lors de la suppression");
        } finally {
            setIsDeleting(false);
        }
    };

    const handleDeleteMany = async () => {
        if (!itemsToDelete || itemsToDelete.length === 0) return;
        setIsDeleting(true);
        try {
            await deleteManyCategories(itemsToDelete);
            setItemsToDelete(null);
            alertSuccess(`${itemsToDelete.length} catégorie(s) supprimée(s)`);
        } catch (err) {
            alertError("Erreur lors de la suppression multiple");
        } finally {
            setIsDeleting(false);
        }
    };

    const handleMarkFeatured = async (selectedCategories: Category[]) => {
        setItemsToFeature(selectedCategories);
        setShowFeatureConfirm(true);
    };

    const confirmMarkFeatured = async () => {
        if (!itemsToFeature || itemsToFeature.length === 0) return;
        setIsFeaturing(true);
        try {
            const promises = itemsToFeature.map((cat) => 
                updateCategory({ isFeatured: true , id: cat.id })
            );
            await Promise.all(promises);
            alertSuccess(`${itemsToFeature.length} catégorie(s) mise(s) en vedette`);
            setItemsToFeature(null);
        } catch (err) {

            alertError("Erreur lors de la mise en vedette");
        } finally {
            setIsFeaturing(false);
            setShowFeatureConfirm(false);
        }
    };

    const handleUnmarkFeatured = async (selectedCategories: Category[]) => {
        setItemsToUnfeature(selectedCategories);
        setShowUnfeatureConfirm(true);
    };

    const confirmUnmarkFeatured = async () => {
        if (!itemsToUnfeature || itemsToUnfeature.length === 0) return;
        setIsFeaturing(true);
        try {
            const promises = itemsToUnfeature.map((cat) => 
                updateCategory( { isFeatured: false ,  id: cat.id })
            );
            await Promise.all(promises);
            alertSuccess(`${itemsToUnfeature.length} catégorie(s) retirée(s) des vedettes`);
            setItemsToUnfeature(null);
        } catch (err) {
            alertError("Erreur lors du retrait des vedettes");
        } finally {
            setIsFeaturing(false);
            setShowUnfeatureConfirm(false);
        }
    };

    const handleToggleFeatured = async (categoryId: string, currentStatus: boolean) => {
        try {
            await updateCategory( { isFeatured: !currentStatus , id: categoryId });
            alertSuccess(currentStatus ? "Retirée des vedettes" : "Ajoutée aux vedettes");
        } catch (err) {
            alertError("Erreur lors de la mise à jour");
        }
    };

    const clearFilters = () => {
        setSearchTerm("");
    };

    const hasActiveFilters = searchTerm;

    return (
        <PageLayout
            title="Gestion des catégories"
            description={`${filteredCategories.length} catégorie${filteredCategories.length > 1 ? 's' : ''} affiché${filteredCategories.length > 1 ? 's' : ''}${sectorSlug ? ` pour ce secteur` : ''}`}
            actions={
                <FlexContainer className="animate-fade-in-up w-full">
                    <button
                        onClick={() => navigate(`/${role}/categories/new`)}
                        className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-primary to-primary/80 text-white rounded-xl"
                    >
                        <Plus size={18} /> Ajouter
                    </button>

                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className={`w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 rounded-xl transition-all ${showFilters ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                    >
                        <Filter size={18} /> Filtrer
                        {hasActiveFilters && <span className="ml-1 w-2 h-2 bg-red-500 rounded-full" />}
                    </button>
                </FlexContainer>
            }
        >
            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-5">
                    <p className="text-xs font-semibold text-blue-700/70">Total catégories</p>
                    <p className="text-2xl font-bold text-blue-700">{stats.total}</p>
                </div>
                <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-5">
                    <p className="text-xs font-semibold text-yellow-700/70">En vedette</p>
                    <p className="text-2xl font-bold text-yellow-700">{stats.featured}</p>
                </div>
                {sectorSlug && (
                    <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-5">
                        <p className="text-xs font-semibold text-orange-700/70">Catégories filtrées</p>
                        <p className="text-2xl font-bold text-orange-700">{stats.filteredTotal}</p>
                    </div>
                )}
            </div>

            {/* Recherche */}
            <div className="relative mb-4">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                    type="text"
                    placeholder="Rechercher une catégorie..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                />
            </div>

            {/* Filtres */}
            {showFilters && (
                <div className="bg-gray-50 rounded-xl p-4 mb-4 space-y-4">
                    <div className="flex items-center justify-between">
                        <h3 className="font-medium text-gray-700">Filtres</h3>
                        {hasActiveFilters && (
                            <button onClick={clearFilters} className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1">
                                <X size={14} /> Effacer tout
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Liste */}
            <SectorsTabLayout>
                <CategoriesList
                    categories={filteredCategories}
                    selectable={true}
                    onAction={(c) => setSelectedCategory(c)}
                    selectActions={[
                        {
                            label: "Mettre en vedette",
                            onClick: handleMarkFeatured,
                            className: "bg-green-600 text-white"
                        },
                        {
                            label: "Retirer des vedettes",
                            onClick: handleUnmarkFeatured,
                            className: "bg-yellow-600 text-white"
                        },
                        {
                            label: "Supprimer",
                            onClick: (selected) => setItemsToDelete(selected.map(i => i.id)),
                            className: "bg-red-600 text-white"
                        }
                    ]}
                />
            </SectorsTabLayout>

            {/* Menu modal */}
            {selectedCategory && (
                <MenuModal
                    title={selectedCategory.name}
                    isOpen={!!selectedCategory}
                    onClose={() => setSelectedCategory(null)}
                    icon={<Tag className="text-primary" size={20} />}
                    menu={[
                        { 
                            label: selectedCategory.isFeatured ? "Retirer des vedettes" : "Mettre en vedette", 
                            icon: Star, 
                            onClick: () => {
                                handleToggleFeatured(selectedCategory.id , selectedCategory.isFeatured || false);
                                setSelectedCategory(null);
                            } 
                        },
                        { label: "Modifier", icon: Tag, onClick: () => navigate(`/${role}/categories/update?id=${selectedCategory.id}`) },
                        { label: "Supprimer", icon: MoreVertical, onClick: () => { setCategoryToDelete(selectedCategory); setSelectedCategory(null); } }
                    ]}
                />
            )}

            {/* Modals suppression */}
            <DeleteConfirmationModal
                isOpen={!!categoryToDelete}
                onClose={() => setCategoryToDelete(null)}
                onConfirm={handleDelete}
                title="Supprimer la catégorie"
                message={`Supprimer "${categoryToDelete?.name}" ?`}
                confirmText={isDeleting ? "Suppression..." : "Supprimer"}
            />

            <DeleteConfirmationModal
                isOpen={!!itemsToDelete && itemsToDelete.length > 0}
                onClose={() => setItemsToDelete(null)}
                onConfirm={handleDeleteMany}
                title="Suppression multiple"
                message={`Supprimer ${itemsToDelete?.length} catégorie(s) ?`}
                confirmText={isDeleting ? "Suppression..." : "Supprimer"}
            />

            {/* Modal confirmation mise en vedette */}
            <DeleteConfirmationModal
                isOpen={showFeatureConfirm}
                onClose={() => setShowFeatureConfirm(false)}
                onConfirm={confirmMarkFeatured}
                title="Mettre en vedette"
                message={`Mettre ${itemsToFeature?.length} catégorie(s) en vedette ?`}
                confirmText={isFeaturing ? "Traitement..." : "Confirmer"}
            />

            {/* Modal confirmation retrait vedette */}
            <DeleteConfirmationModal
                isOpen={showUnfeatureConfirm}
                onClose={() => setShowUnfeatureConfirm(false)}
                onConfirm={confirmUnmarkFeatured}
                title="Retirer des vedettes"
                message={`Retirer ${itemsToUnfeature?.length} catégorie(s) des vedettes ?`}
                confirmText={isFeaturing ? "Traitement..." : "Confirmer"}
            />
        </PageLayout>
    );
}